import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Card,
    CardMedia,
    CardContent,
    Typography,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    IconButton,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import axios from "axios";
import Navbar from "../component/Navbar";
import BottomBar from "../component/BottomBar";

const Playlist = () => {
    const audioRefs = useRef([]);
    const [singers, setSingers] = useState([]);
    const [songs, setSongs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSinger, setSelectedSinger] = useState("");
    const [openDialog, setOpenDialog] = useState(false);
    const [isPlayingIndex, setIsPlayingIndex] = useState(null);

    useEffect(() => {
        axios
            .get("https://music-endpoint.onrender.com/singers/getsingers")
            .then((res) => {
                setSingers(res.data.data || []);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching singers", err);
                setLoading(false);
            });
    }, []);

    // When songs change, reset audioRefs and playing index
    useEffect(() => {
        audioRefs.current = [];
        setIsPlayingIndex(null);
    }, [songs]);

    const handleCardClick = (singer) => {
        axios
            .get(`https://music-endpoint.onrender.com/singers/playlist/${encodeURIComponent(singer.singerName)}`)
            .then((res) => {
                setSongs(res.data.data || []);
                setSelectedSinger(singer.singerName);
                setOpenDialog(true);
            })
            .catch((err) => {
                console.error("Error fetching songs by singer", err);
                setSongs([]);
                setSelectedSinger("");
                setOpenDialog(false);
            });
    };

    const handlePlayPause = async (index) => {
        const currentAudio = audioRefs.current[index];
        if (!currentAudio) return;

        if (isPlayingIndex === index) {
            currentAudio.pause();
            setIsPlayingIndex(null);
        } else {
            if (isPlayingIndex !== null && audioRefs.current[isPlayingIndex]) {
                audioRefs.current[isPlayingIndex].pause();
            }

            try {
                await currentAudio.play();
                setIsPlayingIndex(index);
            } catch (error) {
                console.error("Error playing audio:", error);
            }
        }
    };

    const handleNext = () => {
        if (isPlayingIndex === null) return;
        const nextIndex = (isPlayingIndex + 1) % songs.length;
        handlePlayPause(nextIndex);
    };

    const handlePrev = () => {
        if (isPlayingIndex === null) return;
        const prevIndex = (isPlayingIndex - 1 + songs.length) % songs.length;
        handlePlayPause(prevIndex);
    };

    return (
        <Box>
            <Navbar />
            {loading ? (
                <Typography variant="h6" sx={{ textAlign: "center", marginTop: "20px" }}>
                    Loading singers...
                </Typography>
            ) : (
                <Grid
                    container
                    spacing={3}
                    sx={{ padding: 2, marginBottom: 7, overflowY: "auto", height: "90vh" }}
                >
                    {singers.map((singer) => (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            xl={2}
                            key={singer.id}
                            onClick={() => handleCardClick(singer)}
                        >
                            <Card
                                sx={{
                                    backgroundColor: "#1E1E1E",
                                    color: "#F9FAFB",
                                    borderRadius: 3,
                                    boxShadow: 3,
                                    width: "250px",
                                    height: 280,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    cursor: "pointer",
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    height="220"
                                    image={`https://music-endpoint.onrender.com/singers/singerimage/${singer.id}`}
                                    alt={singer.singerName}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "/fallback-image.jpg";
                                    }}
                                />
                                <CardContent>
                                    <Typography variant="subtitle1" noWrap align="center">
                                        {singer.singerName}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Dialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    // Pause any playing audio when dialog closes
                    if (isPlayingIndex !== null && audioRefs.current[isPlayingIndex]) {
                        audioRefs.current[isPlayingIndex].pause();
                        setIsPlayingIndex(null);
                    }
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{selectedSinger}'s Songs</DialogTitle>
                <DialogContent>
                    {songs.length === 0 ? (
                        <Typography>No songs available</Typography>
                    ) : (
                        <List>
                            {songs.map((song, index) => (
                                <ListItem key={index} disablePadding>
                                    <ListItemText primary={song.songName} />
                                    <IconButton edge="end" onClick={() => handlePlayPause(index)}>
                                        {isPlayingIndex === index ? <PauseIcon /> : <PlayArrowIcon />}
                                    </IconButton>
                                    <audio
                                        ref={(el) => (audioRefs.current[index] = el)}
                                        src={`https://music-endpoint.onrender.com/songs/play/${encodeURIComponent(song.songName)}`}
                                        preload="none"
                                        onEnded={() => setIsPlayingIndex(null)}
                                        onError={(e) => console.error(`Error loading audio for ${song.songName}:`, e)}
                                    />
                                </ListItem>
                            ))}
                        </List>

                    )}
                </DialogContent>
            </Dialog>

            <BottomBar
                audioRef={isPlayingIndex !== null ? audioRefs.current[isPlayingIndex] : null}
                track={isPlayingIndex !== null ? songs[isPlayingIndex] : null}
                isPlaying={isPlayingIndex !== null}
                onPlayPause={() => {
                    if (isPlayingIndex !== null) {
                        handlePlayPause(isPlayingIndex);
                    }
                }}
                onNext={handleNext}
                onPrev={handlePrev}
            />
        </Box>
    );
};

export default Playlist;
